import { PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import {
  AmbientLight,
  Box3,
  DirectionalLight,
  GLSL3,
  Group,
  Material,
  Mesh,
  Object3D,
  PCFSoftShadowMap,
  PlaneGeometry,
  RawShaderMaterial,
  Scene,
  Texture,
  Vector3,
  WebGLRenderer,
} from "three";
import { SugarElement } from "../configurator/SugarCanvas";
import { CameraInterface, Engine, Entity } from "../engine/Engine";
import { SugarModel } from "../loader/SugarModel";
import { ProgressiveLightMap } from "../light/ProgressiveLightMap.js";

const GROUND_NAME = "PLMGround";
const LIGHT_COUNT = 12;
const SHADOW_MAP_RES = 1024;
const LIGHTMAP_RES = 1024;
const BLEND_WINDOW = 15;
const GROUND_SIZE = 2.2;
/** PLM iç sahnesinde ambient yoksa bake çıktısı ~1’e çıkmaz; lightmap indirect’e eklendiği için zemin gri kalır. */
const PLM_BAKE_AMBIENT_INTENSITY = .0;

/** uvMat bake atlası: PLM `uv1` ile aynı koordinatlarda örneklenir; aydınlatma yok. */
const GROUND_LM_VERT = /* glsl */ `
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
in vec3 position;
in vec2 uv1;
out vec2 vLmUv;

void main() {
  vLmUv = uv1;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const GROUND_LM_FRAG = /* glsl */ `
precision highp float;
precision highp int;

uniform sampler2D map;
in vec2 vLmUv;
out vec4 fragColor;

void main() {
  vec4 c = texture(map, vLmUv);
  fragColor = vec4(c.rgb, 1.0 - c.r);
}
`;

type PLMContainers = { object: Object3D; basicMat: Material }[];

type ProgressiveLightMapWithDebug = ProgressiveLightMap & {
  labelMesh: Mesh | null;
};

/**
 * three.js progressive lightmap ([örnek](https://github.com/mrdoob/three.js/blob/master/examples/webgl_shadowmap_progressive.html)):
 * yumuşak gölge zeminde birikir; model mesh’leri bake’e girer ama lightMap sadece zeminde kalır.
 */
class ProgressiveLightmapGroundEntity extends Entity {
  constructor(private readonly host: ProgressiveLightmapGroundElement) {
    super();
  }

  private _plm?: ProgressiveLightMap;
  private _dirLights: DirectionalLight[] = [];
  private _plmAmbient?: AmbientLight;
  private _ground?: Mesh;
  private _lightOrigin?: Group;
  private _lightTarget?: Group;
  private _built = false;
  private _accumulate = true;

  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private parseDebugPosition(s: string): Vector3 {
    const p = s.split(",").map((x) => parseFloat(x.trim()));
    return new Vector3(
      Number.isFinite(p[0]) ? p[0] : 0,
      Number.isFinite(p[1]) ? p[1] : 2.2,
      Number.isFinite(p[2]) ? p[2] : 0.5,
    );
  }

  /** Host attribute’ları değişince (debug aç/kapa, konum, ölçek). */
  syncDebugLightmapFromHost(): void {
    if (!this._plm) return;
    const plm = this._plm as ProgressiveLightMapWithDebug;
    if (this.host.debugLightmap) {
      const pos = this.parseDebugPosition(this.host.debugLightmapPosition);
      this._plm.showDebugLightmap(true, pos);
      if (plm.labelMesh) {
        const sc = this.host.debugLightmapScale;
        plm.labelMesh.scale.set(sc, sc, 1);
        plm.labelMesh.renderOrder = 9999;
      }
    } else {
      plm.showDebugLightmap(false);
    }
  }

  private _interactionStart = () => {
    this._accumulate = false;
  };
  private _interactionEnd = () => {
    this._accumulate = true;
  };

  private _queueRebuild = () => {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      this.tryBuild();
    }, 350);
  };

  override onAdd(engine: Engine): void {
    super.onAdd(engine);
    engine.addEventListener("scene-first-updated", this._queueRebuild);
    engine.addEventListener("model-added", this._queueRebuild);
    engine.addEventListener("model-created", this._queueRebuild);
    engine.addEventListener("scene-mutated", this._queueRebuild);
    //engine.addEventListener("interaction-start", this._interactionStart);
    //engine.addEventListener("interaction-end", this._interactionEnd);
    engine.addEventListener("shadow-reset", this._onShadowReset);
    if (engine.canvas) {
      engine.canvas.addEventListener("configurator-updated", this._queueRebuild);
    }
  }

  override onRemove(engine: Engine): void {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    engine.removeEventListener("scene-first-updated", this._queueRebuild);
    engine.removeEventListener("model-added", this._queueRebuild);
    engine.removeEventListener("model-created", this._queueRebuild);
    engine.removeEventListener("scene-mutated", this._queueRebuild);
    //engine.removeEventListener("interaction-start", this._interactionStart);
    //engine.removeEventListener("interaction-end", this._interactionEnd);
    engine.removeEventListener("shadow-reset", this._onShadowReset);
    if (engine.canvas) {
      engine.canvas.removeEventListener(
        "configurator-updated",
        this._queueRebuild,
      );
    }
    this.disposeProgressive();
  }

  private _onShadowReset = () => {
    this._built = false;
    this.disposeProgressive();
    this._queueRebuild();
  };

  private collectConfiguratorMeshes(scene: Scene): Mesh[] {
    const roots: SugarModel[] = [];
    scene.traverse((o) => {
      if (o instanceof SugarModel) roots.push(o);
    });
    const seen = new Set<string>();
    const meshes: Mesh[] = [];
    for (const root of roots) {
      root.traverse((o) => {
        if (o instanceof Mesh && o.geometry?.getAttribute("uv")) {
          if (seen.has(o.uuid)) return;
          seen.add(o.uuid);
          o.castShadow = true;
          meshes.push(o);
        }
      });
    }
    return meshes;
  }

  private detachLightsFromPlmScene(plm: ProgressiveLightMap): void {
    const internal = (plm as unknown as { scene?: Scene }).scene;
    if (!internal) return;
    if (this._plmAmbient) {
      internal.remove(this._plmAmbient);
      this._plmAmbient.dispose();
      this._plmAmbient = undefined;
    }
    for (const l of this._dirLights) {
      internal.remove(l);
      l.dispose();
    }
    this._dirLights = [];
  }

  private disposeProgressive(): void {
    if (this._plm) {
      const plm = this._plm as ProgressiveLightMapWithDebug;
      plm.showDebugLightmap(false);
      if (plm.labelMesh) {
        plm.labelMesh.removeFromParent();
      }
      this.detachLightsFromPlmScene(this._plm);
      this._plm.dispose();
      this._plm = undefined;
    }
    const scene = this.engine?.activeScene;
    if (this._ground) {
      scene?.remove(this._ground);
      this._ground.geometry.dispose();
      (this._ground.material as RawShaderMaterial).dispose();
      this._ground = undefined;
    }
    if (this._lightOrigin) {
      scene?.remove(this._lightOrigin);
      this._lightOrigin = undefined;
    }
    if (this._lightTarget) {
      scene?.remove(this._lightTarget);
      this._lightTarget = undefined;
    }
    this._dirLights = [];
  }

  private stripLightmapExceptGround(plm: ProgressiveLightMap, ground: Mesh): void {
    const containers = (plm as unknown as { lightMapContainers?: PLMContainers })
      .lightMapContainers;
    if (!containers?.length) return;
    for (const c of containers) {
      if (c.object === ground) continue;
      const m = c.basicMat as Material & { lightMap?: unknown };
      m.lightMap = null;
      m.needsUpdate = true;
    }
  }

  private tryBuild(): void {
    const engine = this.engine;
    const scene = engine?.activeScene;
    const renderer = engine?.renderer.instance as WebGLRenderer | undefined;
    if (!engine || !scene || !renderer) return;

    const meshes = this.collectConfiguratorMeshes(scene);
    if (meshes.length === 0) return;

    if (this._built) return;

    this.disposeProgressive();

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = PCFSoftShadowMap;

    this._lightOrigin = new Group();
    this._lightOrigin.position.set(0, 2.5, 0);
    scene.add(this._lightOrigin);

    this._lightTarget = new Group();
    this._lightTarget.position.set(0, 0.4, 0);
    scene.add(this._lightTarget);

    for (let l = 0; l < LIGHT_COUNT; l++) {
      const dir = new DirectionalLight(
        0xffffff,
        Math.PI / LIGHT_COUNT,
      );
      dir.name = `PLM Dir ${l}`;
      dir.position.set(2, 8, 4);
      dir.castShadow = true;
      dir.shadow.camera.near = 0.2;
      dir.shadow.camera.far = 80;
      dir.shadow.camera.left = -1;
      dir.shadow.camera.right = 1;
      dir.shadow.camera.top = 1;
      dir.shadow.camera.bottom = -1;
      dir.shadow.mapSize.width = SHADOW_MAP_RES;
      dir.shadow.mapSize.height = SHADOW_MAP_RES;
      dir.target = this._lightTarget;
      this._dirLights.push(dir);
    }

    this._plmAmbient = new AmbientLight(
      0xffffff,
      PLM_BAKE_AMBIENT_INTENSITY,
    );

    const groundMat = new RawShaderMaterial({
      glslVersion: GLSL3,
      vertexShader: GROUND_LM_VERT,
      fragmentShader: GROUND_LM_FRAG,
      uniforms: {
        map: { value: null },
      },
      transparent: true,
      toneMapped: false,
      depthWrite: false,
    });
    groundMat.name = "PLMGround/LightmapRaw";
    this._ground = new Mesh(
      new PlaneGeometry(GROUND_SIZE, GROUND_SIZE),
      groundMat,
    );
    this._ground.name = GROUND_NAME;
    this._ground.rotation.x = -Math.PI / 2;
    this._ground.position.y = 0;
    this._ground.receiveShadow = true;
    this._ground.castShadow = false;
    this._ground.userData.contributeToAO = "false";
    scene.add(this._ground);

    const toBake: Object3D[] = [
      this._plmAmbient,
      ...this._dirLights,
      this._ground,
      ...meshes,
    ];

    this._plm = new ProgressiveLightMap(renderer, LIGHTMAP_RES);
    this._plm.addObjectsToLightMap(toBake);
    this.stripLightmapExceptGround(this._plm, this._ground);

    const plmRt = this._plm as unknown as {
      progressiveLightMap2: { texture: Texture };
    };
    groundMat.uniforms.map.value = plmRt.progressiveLightMap2.texture;

    this.syncDebugLightmapFromHost();

    this._built = true;
  }

  override animate(
    _dt: number,
    _scene?: Scene,
    camera?: CameraInterface,
  ): void {
    super.animate(_dt, _scene, camera);
    if (!this._plm || !camera?.instance || !this._accumulate) return;

    const engine = this.engine;
    const mainScene = engine?.activeScene;
    if (!mainScene) return;

    const box = new Box3();
    let found = false;
    mainScene.traverse((o) => {
      if (o instanceof SugarModel) {
        box.expandByObject(o);
        found = true;
      }
    });
    if (found && !box.isEmpty()) {
      const c = new Vector3();
      box.getCenter(c);
      this._lightTarget?.position.set(c.x, Math.max(0.05, box.min.y + 0.02), c.z);
      this._lightOrigin?.position.set(c.x , c.y + 4, c.z );
    }

    const lightRadius = 15;
    const ambientWeight = 0.75;
    for (let l = 0; l < this._dirLights.length; l++) {
      const lo = this._lightOrigin!;
      if (Math.random() > ambientWeight) {
        this._dirLights[l].position.set(
          lo.position.x + Math.random() * lightRadius,
          lo.position.y + Math.random() * lightRadius,
          lo.position.z + Math.random() * lightRadius,
        );
      } else {
        const lambda = Math.acos(2 * Math.random() - 1) - Math.PI / 2;
        const phi = 2 * Math.PI * Math.random();
        const lt = this._lightTarget!.position;
        this._dirLights[l].position.set(
          Math.cos(lambda) * Math.cos(phi) * 12 + lt.x,
          Math.abs(Math.cos(lambda) * Math.sin(phi) * 12) + lt.y + 2,
          Math.sin(lambda) * 12 + lt.z,
        );
      }
    }

    this._plm.update(camera.instance, BLEND_WINDOW, true);
  }
}

@customElement("progressive-lightmap-ground")
export class ProgressiveLightmapGroundElement extends SugarElement {
  /**
   * Lightmap dokusunu sahnede düzlem olarak gösterir.
   * HTML: `debug-lightmap` veya `debug-lightmap="true"`
   */
  @property({ type: Boolean, attribute: "debug-lightmap", reflect: true })
  debugLightmap = false;

  /** Debug düzlemin ölçeği (bir kenar çarpanı). */
  @property({ type: Number, attribute: "debug-lightmap-scale" })
  debugLightmapScale = 2.5;

  /** Virgülle ayrılmış dünya konumu `x,y,z` (örn. `0,2.2,0.5`). */
  @property({ type: String, attribute: "debug-lightmap-position" })
  debugLightmapPosition = "0,2.2,0.5";

  createEntity(): Entity | undefined {
    return new ProgressiveLightmapGroundEntity(this);
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (
      changed.has("debugLightmap") ||
      changed.has("debugLightmapScale") ||
      changed.has("debugLightmapPosition")
    ) {
      (this.entity as ProgressiveLightmapGroundEntity | undefined)?.syncDebugLightmapFromHost();
    }
  }
}
